import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSwitchRole } from "@/lib/useSwitchRole";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  TrendingUp, LayoutDashboard, Hammer, QrCode, Wallet, FileText, UserRound, MapPin, MoreHorizontal, GraduationCap,
  Building2, Users, Landmark, Tag, Images, Settings2, MessageCircleQuestion, Flag, Instagram, Menu, MessagesSquare, Sun, Megaphone,
} from "lucide-react";

export const ADMIN_TABS = [
  { value: "metricas", label: "Métricas", icon: TrendingUp },
  { value: "negocios", label: "Maestros", icon: Building2 },
  { value: "usuarios", label: "Usuarios", icon: Users },
  { value: "mensajes", label: "Mensajes", icon: MessagesSquare },
  { value: "denuncias", label: "Denuncias", icon: Flag },
  { value: "uf", label: "Valor UF", icon: Landmark, group: "Plataforma" },
  { value: "categorias", label: "Categorías", icon: Tag, group: "Plataforma" },
  { value: "comunidad", label: "Comunidad", icon: Images, group: "Plataforma" },
  { value: "configuracion", label: "Configuración", icon: Settings2, group: "Plataforma" },
];

export const MAESTRO_TABS = [
  { value: "hoy", label: "Hoy", icon: Sun },
  { value: "resumen", label: "Resumen", icon: TrendingUp },
  { value: "panel", label: "Panel", icon: LayoutDashboard },
  { value: "talleres", label: "Talleres", icon: Hammer },
  { value: "qr", label: "Escáner QR", icon: QrCode, group: "Operación" },
  { value: "comunidad", label: "Comunidad", icon: Megaphone, group: "Operación" },
  { value: "preguntas", label: "Preguntas", icon: MessageCircleQuestion, group: "Operación" },
  { value: "instagram", label: "Instagram", icon: Instagram, group: "Operación" },
  { value: "estado", label: "Billetera", icon: Wallet, group: "Mi cuenta" },
  { value: "politicas", label: "Políticas", icon: FileText, group: "Mi cuenta" },
  { value: "presentacion", label: "Presentación", icon: UserRound, group: "Mi cuenta" },
  { value: "direcciones", label: "Direcciones", icon: MapPin, group: "Mi cuenta" },
  { value: "configuracion", label: "Configuración", icon: Settings2, group: "Mi cuenta" },
];

export function DesktopMoreMenu({ tabs, main, value, onChange, testId, itemPrefix }) {
  const rest = tabs.filter((t) => !main.includes(t.value));
  const active = rest.find((t) => t.value === value);
  const groups = [...new Set(rest.map((t) => t.group))];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={active ? "default" : "outline"} className="gap-2 shrink-0" data-testid={testId} aria-label="Más opciones">
          {active && <><active.icon className="w-4 h-4" /><span data-testid={`${testId}-current`}>{active.label}</span></>}
          <Menu className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {groups.map((g, i) => (
          <div key={g}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">{g}</DropdownMenuLabel>
            {rest.filter((t) => t.group === g).map((t) => (
              <DropdownMenuItem key={t.value} onSelect={() => onChange(t.value)} data-testid={`${itemPrefix}${t.value}`}
                className={`gap-2 cursor-pointer ${value === t.value ? "bg-primary/10 text-primary font-semibold" : ""}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavBtn({ active, icon: Icon, label, onClick, testId, iconClass = "", badge = 0 }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-current={active ? "page" : undefined}
      className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 pt-2 pb-2.5 px-1 text-[11px] leading-tight font-medium transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>
      <span className={`relative grid place-items-center w-12 h-8 rounded-full transition-colors ${active ? "bg-primary/10" : ""}`}><Icon className={`w-[22px] h-[22px] ${iconClass}`} />
        {badge > 0 && <span className="absolute -top-1 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center" data-testid={`${testId}-badge`}>{badge}</span>}
      </span>
      <span className="truncate max-w-full">{label}</span>
    </button>
  );
}

export function BottomBar({ children, testId }) {
  return (
    <>
      <div className="h-20 lg:hidden" aria-hidden />
      <nav data-testid={testId}
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="flex max-w-xl mx-auto">{children}</div>
      </nav>
    </>
  );
}
export { NavBtn };

export function MaestroMobileNav({ value, onChange }) {
  const { user } = useAuth();
  const switchRole = useSwitchRole();
  const actions = user?.can_switch_role
    ? [{ key: "switch", label: "Mi perfil alumno", icon: GraduationCap, onClick: () => switchRole("customer"), testId: "mnav-switch-alumno" }]
    : [];
  return <SectionMobileNav tabs={MAESTRO_TABS} value={value} onChange={onChange} testId="maestro-bottom-nav" actions={actions} />;
}

export function MoreSheet({ open, onOpenChange, groups }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="mnav-more-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>Más opciones</SheetTitle>
          <SheetDescription>Elige dónde quieres ir</SheetDescription>
        </SheetHeader>
        {groups.filter((g) => g.items.length).map((g) => (
          <div key={g.title} className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{g.title}</p>
            <div className="grid grid-cols-2 gap-2">
              {g.items.map((t) => (
                <button key={t.key} type="button" onClick={t.onClick} data-testid={t.testId}
                  className={`relative flex items-center gap-2.5 rounded-2xl border p-3 min-h-[52px] text-sm font-medium text-left transition-colors ${t.active ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                  <t.icon className={`w-5 h-5 shrink-0 ${t.iconClass || ""}`} />{t.label}
                  {t.badge > 0 && <span className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-coral text-white text-[11px] font-bold grid place-items-center">{t.badge}</span>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </SheetContent>
    </Sheet>
  );
}

export function SectionMobileNav({ tabs, value, onChange, testId, actions = [], badges = {} }) {
  const MAIN = tabs.filter((t) => !t.group);
  const MORE = tabs.filter((t) => t.group);
  const [open, setOpen] = useState(false);
  const inMore = MORE.some((t) => t.value === value);
  const go = (v) => { onChange(v); setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const groups = [...new Set(MORE.map((t) => t.group))];
  return (
    <>
      <BottomBar testId={testId}>
        {MAIN.map((t) => <NavBtn key={t.value} active={value === t.value} icon={t.icon} label={t.label} onClick={() => go(t.value)} testId={`mnav-${t.value}`} badge={badges[t.value]} />)}
        <NavBtn active={inMore || open} icon={MoreHorizontal} label="Más" onClick={() => setOpen(true)} testId="mnav-more" />
      </BottomBar>
      <MoreSheet open={open} onOpenChange={setOpen} groups={[
        ...groups.map((g) => ({ title: g, items: MORE.filter((t) => t.group === g).map((t) => ({
          key: t.value, label: t.label, icon: t.icon, active: value === t.value, onClick: () => go(t.value), testId: `mnav-${t.value}` })) })),
        { title: "Cambiar perfil", items: actions.map((a) => ({ ...a, onClick: () => { setOpen(false); a.onClick(); } })) },
      ]} />
    </>
  );
}
